import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useAtom } from 'jotai';
import { trendingTopicsApi } from '../../../apis/trending-topics.api';
import { profileAtom } from '../../Settings/atom';

const StoryPulseCompact = () => {
  const navigate = useNavigate();
  const [profile] = useAtom(profileAtom);
  const [loading, setLoading] = useState(true);
  const [trendingStories, setTrendingStories] = useState([]);

  useEffect(() => {
    const getTrendingStories = async () => {
      try {
        const result = await trendingTopicsApi.getPublishedTrendingTopics(null, 3);
        if (result) {
          setTrendingStories(result.data || []);
        }
      } catch (error) {
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    getTrendingStories();
  }, []);

  const getOpportunityBadge = (urgencyIndicator, readerDemand) => {
    const isLowCoverage = !readerDemand || readerDemand === 'LOW';
    const isPeaking = urgencyIndicator === 'PEAKING';
    const isRising = urgencyIndicator === 'RISING';

    if (isLowCoverage && (isPeaking || isRising)) {
      return { text: 'High opportunity', dotClass: 'bg-growth-green-500', textClass: 'text-growth-green-600' };
    } else if (isLowCoverage) {
      return { text: 'Under-covered', dotClass: 'bg-growth-green-500', textClass: 'text-growth-green-600' };
    } else if (isPeaking) {
      return { text: 'Trending', dotClass: 'bg-orange-500', textClass: 'text-orange-600' };
    } else if (isRising) {
      return { text: 'Rising', dotClass: 'bg-blue-500', textClass: 'text-blue-600' };
    }
    return { text: 'Open', dotClass: 'bg-gray-400', textClass: 'text-gray-500' };
  };

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-6 h-6 bg-gray-200 rounded-full"></div>
          <div className="w-20 h-4 bg-gray-200 rounded"></div>
        </div>
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="space-y-1">
              <div className="w-full h-3 bg-gray-200 rounded"></div>
              <div className="w-2/3 h-3 bg-gray-200 rounded"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (trendingStories.length === 0) {
    return (
      <div>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-6 h-6 rounded-full bg-gradient-to-br from-growth-green-400 to-growth-green-600 flex items-center justify-center">
            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M13.5.67s.74 2.65.74 4.8c0 2.06-1.35 3.73-3.41 3.73-2.07 0-3.63-1.67-3.63-3.73l.03-.36C5.21 7.51 4 10.62 4 14c0 4.42 3.58 8 8 8s8-3.58 8-8C20 8.61 17.41 3.8 13.5.67z"/>
            </svg>
          </div>
          <h3 className="text-base font-bold text-gray-900">Story Pulse</h3>
        </div>
        <p className="text-xs text-gray-400">No stories right now</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-full bg-gradient-to-br from-growth-green-400 to-growth-green-600 flex items-center justify-center">
            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M13.5.67s.74 2.65.74 4.8c0 2.06-1.35 3.73-3.41 3.73-2.07 0-3.63-1.67-3.63-3.73l.03-.36C5.21 7.51 4 10.62 4 14c0 4.42 3.58 8 8 8s8-3.58 8-8C20 8.61 17.41 3.8 13.5.67z"/>
            </svg>
          </div>
          <h3 className="text-base font-bold text-gray-900">Story Pulse</h3>
        </div>
      </div>

      <p className="text-xs text-gray-500 mb-4">
        Under-covered stories waiting for your take
      </p>

      {/* Stories list */}
      <div className="space-y-4">
        {trendingStories.slice(0, 3).map((story, index) => {
          const badge = getOpportunityBadge(story.urgencyIndicator, story.readerDemand);
          return (
            <div 
              key={story.id || index}
              className="group"
            >
              {/* Badge */}
              <div className="flex items-center gap-1.5 mb-1">
                <span className={`w-1.5 h-1.5 rounded-full ${badge.dotClass}`}></span>
                <span className={`text-[10px] font-semibold uppercase tracking-wide ${badge.textClass}`}>
                  {badge.text}
                </span>
              </div>
              
              {/* Title */}
              <p 
                className="text-sm font-medium text-gray-800 group-hover:text-growth-green-600 transition-colors leading-snug line-clamp-2 cursor-pointer mb-1.5"
                onClick={() => navigate('/?tab=newsHub')}
              >
                {story.topic}
              </p>

              {/* Write CTA - only for creators */}
              {profile?.isCreator && (
                <button
                  onClick={() => navigate(`/article/create?storyId=${encodeURIComponent(story.id)}`)}
                  className="text-xs font-semibold text-growth-green-600 hover:text-growth-green-700 transition-colors flex items-center gap-1"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                  </svg>
                  Write about this
                </button>
              )}
            </div>
          );
        })}
      </div>

      {/* View all link */}
      <button
        onClick={() => navigate('/?tab=newsHub')}
        className="mt-4 w-full text-xs font-semibold text-gray-500 hover:text-growth-green-600 transition-colors flex items-center justify-center gap-1 py-2 border border-gray-200 rounded-lg hover:border-growth-green-300"
      >
        View all stories
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </button>
    </div>
  );
};

export default StoryPulseCompact;
